import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';

const CallCenter = ({ identity }) => {
  const [contacts, setContacts] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [busyId, setBusyId] = useState('');
  const audioContextRef = useRef(null);
  const validIdentity = identity?.type && identity?.id;

  const refreshCalls = useCallback(async () => {
    if (!validIdentity) return;
    try {
      const type = encodeURIComponent(identity.type);
      const id = encodeURIComponent(identity.id);
      const [incomingResponse, outgoingResponse] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/calls/incoming/${type}/${id}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/calls/outgoing/${type}/${id}`)
      ]);
      if (incomingResponse.ok) setIncoming(await incomingResponse.json());
      if (outgoingResponse.ok) setOutgoing(await outgoingResponse.json());
    } catch (error) {
      console.error('Unable to refresh calls:', error);
    }
  }, [identity?.id, identity?.type, validIdentity]);

  useEffect(() => {
    if (!validIdentity) return undefined;
    fetch(`${API_ENDPOINTS.BASE_URL}/api/calls/contacts?type=${encodeURIComponent(identity.type)}&id=${encodeURIComponent(identity.id)}`)
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Unable to load contacts')))
      .then(setContacts)
      .catch(console.error);
    refreshCalls();
    const timer = window.setInterval(refreshCalls, 3000);
    return () => window.clearInterval(timer);
  }, [identity?.id, identity?.type, refreshCalls, validIdentity]);

  useEffect(() => {
    if (!incoming.length) return undefined;
    const ring = () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const context = audioContextRef.current || new AudioContext();
        audioContextRef.current = context;
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = 520;
        gain.gain.setValueAtTime(0.0001, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.55);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.6);
      } catch (error) {
        // Sound can be blocked until the user first interacts with the page.
      }
    };
    ring();
    const timer = window.setInterval(ring, 1400);
    return () => window.clearInterval(timer);
  }, [incoming.length]);

  const filteredContacts = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term
      ? contacts.filter(contact => `${contact.name} ${contact.type} ${contact.className || ''}`.toLowerCase().includes(term))
      : contacts;
  }, [contacts, query]);

  const updateStatus = async (call, status) => {
    setBusyId(call.id);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/calls/${encodeURIComponent(call.id)}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!response.ok) throw new Error('Unable to update call');
      if (status === 'accepted') window.open(call.meetingUrl, '_blank', 'noopener,noreferrer');
      await refreshCalls();
    } catch (error) {
      console.error(error);
    } finally {
      setBusyId('');
    }
  };

  const startCall = async contact => {
    const callWindow = window.open('', '_blank');
    const contactKey = `${contact.type}:${contact.id}`;
    setBusyId(contactKey);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/calls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callerType: identity.type,
          callerId: identity.id,
          callerName: identity.name,
          recipientType: contact.type,
          recipientId: contact.id,
          recipientName: contact.name
        })
      });
      const call = await response.json();
      if (!response.ok) throw new Error(call.error || 'Unable to start call');
      if (callWindow) callWindow.location = call.meetingUrl;
      else window.open(call.meetingUrl, '_blank', 'noopener,noreferrer');
      setExpanded(false);
      await refreshCalls();
    } catch (error) {
      if (callWindow) callWindow.close();
      console.error(error);
    } finally {
      setBusyId('');
    }
  };

  if (!validIdentity) return null;
  const activeOutgoing = outgoing[0];
  const activeIncoming = incoming[0];

  return (
    <>
      <section id="call-center" className="call-center">
        <div className="call-center__header">
          <div>
            <strong>School calls</strong>
            <small>Call your available school contacts securely.</small>
          </div>
          <button type="button" className="call-center__start" onClick={() => setExpanded(value => !value)}>
            {expanded ? 'Close' : 'Start a call'}
          </button>
        </div>
        {activeOutgoing && (
          <div className="call-center__outgoing">
            <span className="call-center__status-icon">CALL</span>
            <div>
              <strong>{activeOutgoing.status === 'accepted' ? 'Call connected' : `Calling ${activeOutgoing.recipientName}`}</strong>
              <small>{activeOutgoing.status === 'accepted' ? 'Join again if the call window was closed.' : 'Waiting for an answer...'}</small>
            </div>
            <button type="button" onClick={() => window.open(activeOutgoing.meetingUrl, '_blank', 'noopener,noreferrer')}>Join</button>
            <button type="button" className="danger" onClick={() => updateStatus(activeOutgoing, activeOutgoing.status === 'accepted' ? 'ended' : 'cancelled')}>
              End
            </button>
          </div>
        )}
        {expanded && (
          <div className="call-center__directory">
            <label>
              <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search available contacts" />
            </label>
            <div className="call-center__contacts">
              {filteredContacts.map(contact => {
                const key = `${contact.type}:${contact.id}`;
                return (
                  <div className="call-center__contact" key={key}>
                    <span aria-hidden="true">{contact.name?.slice(0, 1).toUpperCase() || 'U'}</span>
                    <div>
                      <strong>{contact.name}</strong>
                      <small>{contact.type}{contact.className ? ` - ${contact.className}` : ''}</small>
                    </div>
                    <button type="button" title={`Call ${contact.name}`} disabled={busyId === key} onClick={() => startCall(contact)}>
                      Call
                    </button>
                  </div>
                );
              })}
              {!filteredContacts.length && <p>No matching contacts are available.</p>}
            </div>
          </div>
        )}
      </section>
      {activeIncoming && (
        <div className="call-ringing">
          <div className="call-ringing__card">
            <span className="call-ringing__icon">CALL</span>
            <small>Incoming {activeIncoming.callerType} call</small>
            <h2>{activeIncoming.callerName}</h2>
            <div>
              <button type="button" className="decline" disabled={busyId === activeIncoming.id} onClick={() => updateStatus(activeIncoming, 'declined')}>
                Decline
              </button>
              <button type="button" className="accept" disabled={busyId === activeIncoming.id} onClick={() => updateStatus(activeIncoming, 'accepted')}>
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CallCenter;
